/* eslint-disable no-console */
import { CheckboxStyle } from "@insite/mobius/Checkbox";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import CheckboxChecked from "@store/Components/Icons/Checkbox/CheckboxChecked";
import CheckboxIndeterminate from "@store/Components/Icons/Checkbox/CheckboxIndeterminate";
import { TYPOGRAPHY } from "@store/theme/design";
import { polyfillCss } from "@store/theme/polyfills";
import { css } from "styled-components";

type CheckboxColor = string | null;

const borderWidth = "2px";

const checkboxStyles = (
    color: CheckboxColor = "secondary",
    hoverColor: CheckboxColor = null,
    backgroundColor: CheckboxColor = "common.background",
) => {
    return css`
        border: ${borderWidth} solid currentColor;
        ${props => (color ? `color: ${resolveColor(color, props.theme)};` : null)}
        ${props => (backgroundColor ? `background-color: ${resolveColor(backgroundColor, props.theme)};` : null)}
        ${props => {
            if (hoverColor) {
                return `
                    &:hover {
                        color: ${resolveColor(hoverColor, props.theme)};
                    }
                `;
            }

            return null;
        }}
    `;
};

export default {
    defaultProps: {
        iconProps: {
            src: CheckboxChecked,
        },
        indeterminateIconProps: {
            src: CheckboxIndeterminate,
        },
        css: polyfillCss`
            align-items: flex-start;
            cursor: default;

            div[role=checkbox] {
				cursor: pointer;
				
                &[aria-checked="true"][aria-disabled] {
                    background: ${getColor("primary")};
                    border: ${getColor("primary")} 1px solid;
                    color: ${getColor("common.background")};
                    fill: ${getColor("common.background")};
                }
            }

            span[role=checkbox] {
                ${checkboxStyles()}
                cursor: pointer;
				margin-top: .1em;

				&[aria-checked=true]:not([aria-disabled=true]), &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                    ${checkboxStyles()}
                }

                &[aria-disabled=true] {
                    ${checkboxStyles("common.disabled", null)}
                    cursor: default;

                    + label {
                        cursor: default;
                    }
                }

                &:focus {
                    box-shadow: none;
                    outline-color: ${getProp("theme.focus.color")};
                    outline-style: ${getProp("theme.focus.style")};
                    outline-width: ${borderWidth};
                    outline-offset: -${borderWidth};
				}

                + label {
                    margin: 0;
                    padding-left: .7em;
                    cursor: pointer;

                    &::before {
                        flex-shrink: 0;
                    }
                }
            }
        `,
    },
    groupDefaultProps: {
        labelProps: {
            size: TYPOGRAPHY.size.small,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: polyfillCss`
            & > ${TypographyStyle}:first-child {
                margin-bottom: 5px;
            }
            
            ${CheckboxStyle} + ${CheckboxStyle} {
                margin-top: 0.5em;
            }
        `,
    },
};
